// File: js/money/day-fold.js
//
// The device-day projection behind the day close. Imports nothing outside `money/` (spec §8).
//
// `expectedCash` is what the drawer should hold: sales minus voids minus discounts, over
// the events attributed to this device and this business date. It is pinned to the
// server's own fold (`v_close_check.server_fold`) by `day-fold.fixture.json`, asserted by
// the unit test here and by the live verification script — one arithmetic, two runtimes,
// so a close variance can only ever mean a real cash difference.

import { dedupeById } from './dedupe.js';
import { foldMoney } from './money-fold.js';

/** Kinds that cancel a sale and therefore carry no drawer of their own. */
const CORRECTION_KINDS = new Set(['void', 'discount']);

/**
 * @typedef {Object} DeviceDayFold
 * @property {number} expectedCash Integer FCFA the drawer should hold.
 * @property {number} saleCount Number of sale events counted.
 * @property {import('./money-fold.js').IgnoredEvent[]} ignored Excluded rows, with reasons.
 */

/**
 * Folds one device-day.
 *
 * **Correction attribution (spec §10.1, corrected 27 July 2026).** A `void` or `discount`
 * is attributed to its target sale's `device_id` **and** `business_date`, both recovered
 * through `reverses_id`. Our corrections are admin-only and online-only, so they are
 * authored on a different device and usually a different day than the cash they cancel:
 * booking one on its own device-day would leave the collecting cashier's close
 * permanently over and invent a shortage on the admin's day. The two dimensions cannot be
 * split — device-from-target with date-from-own produces a device-day for a drawer that
 * may never have been open, with negative expected cash and no close against it.
 *
 * (`v_daily_cash` deliberately books a correction on its **own** date: a cash-position
 * view, a different and separately documented attribution. It is not mirrored here.)
 *
 * Because attribution is resolved through the target, **pass the full event set and let
 * `selector` do the filtering** — a set pre-filtered to one device-day cannot contain the
 * sales its corrections point at. A correction whose target is absent belongs to no
 * drawer we can name: it is excluded from `expectedCash` and surfaced in `ignored[]` as
 * `correction-target-not-in-set`, never silently dropped and never silently counted
 * (invariant 6, and the same discipline as `void-target-not-in-set` in `stock-fold.js`).
 *
 * @param {import('./money-fold.js').MoneyEvent[]} events Ledger rows; ideally the whole
 *   mirror, so every correction can reach its target sale.
 * @param {{deviceId?: string, businessDate?: string}} [selector] Fold-side filter on the
 *   **attributed** drawer and business date — never a server-side filter (spec §8).
 * @returns {DeviceDayFold}
 * @throws {TypeError} If `events` is not an array (programmer error).
 */
export function foldDeviceDay(events, selector = {}) {
    if (!Array.isArray(events)) {
        throw new TypeError(`foldDeviceDay expects an array, received ${typeof events}`);
    }
    const { deviceId, businessDate } = selector;
    const unique = dedupeById(events);

    // Only a sale owns a drawer, so only a sale is a valid correction target. A correction
    // pointing at anything else is unattributable, not silently re-homed.
    const salesById = new Map();
    for (const event of unique) {
        if (event && typeof event === 'object' && event.kind === 'sale' && event.id != null) {
            salesById.set(event.id, event);
        }
    }

    /**
     * @param {*} drawerDevice Attributed device.
     * @param {*} drawerDate Attributed business date.
     * @returns {boolean}
     */
    const inScope = (drawerDevice, drawerDate) =>
        (deviceId == null || drawerDevice === deviceId)
        && (businessDate == null || drawerDate === businessDate);

    const scoped = [];
    /** @type {import('./money-fold.js').IgnoredEvent[]} */
    const unattributed = [];

    for (const event of unique) {
        if (!event || typeof event !== 'object') {
            scoped.push(event); // foldMoney names it `malformed-event`.
            continue;
        }
        if (!CORRECTION_KINDS.has(event.kind)) {
            if (inScope(event.device_id, event.business_date)) scoped.push(event);
            continue;
        }
        const target = salesById.get(event.reverses_id);
        if (!target) {
            unattributed.push({
                id: event.id != null ? event.id : null,
                kind: event.kind,
                amount: Number.isInteger(event.amount) ? event.amount : null,
                reason: 'correction-target-not-in-set'
            });
            continue;
        }
        if (inScope(target.device_id, target.business_date)) scoped.push(event);
    }

    const fold = foldMoney(scoped);
    const saleCount = Object.values(fold.byChannel).reduce((sum, bucket) => sum + bucket.saleCount, 0);

    return {
        expectedCash: fold.net,
        saleCount,
        // Unattributable corrections are appended after the fold's own ignored rows.
        ignored: [...fold.ignored, ...unattributed]
    };
}
